import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Download, RefreshCw, PackageCheck, PackageX } from "lucide-react";

const TOKEN_KEY = "meamor:admin_token:v1";

function formatNZD(amount) {
  return new Intl.NumberFormat("en-NZ", {
    style: "currency",
    currency: "NZD",
  }).format(amount);
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-NZ", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function toCSV(headers, rows) {
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  return (
    headers.join(",") +
    "\n" +
    rows.map((r) => headers.map((h) => esc(r[h])).join(",")).join("\n")
  );
}

function downloadFile(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function LoginView({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    try {
      setLoading(true);
      const res = await api.post("/admin/login", { password });
      const token = res.data?.token;
      if (!token) throw new Error("no token");
      localStorage.setItem(TOKEN_KEY, token);
      onLogin(token);
    } catch (e) {
      const msg =
        e?.response?.data?.detail === "Incorrect password"
          ? "Incorrect password"
          : "Login failed. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#FFFDF9] flex items-center justify-center px-6"
      data-testid="admin-login"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-white border border-[#E8D7D0] rounded-3xl p-8 shadow-[0_30px_60px_-30px_rgba(196,139,159,0.25)]"
      >
        <div className="text-[11px] tracking-[0.3em] uppercase text-[#C48B9F] text-center">
          Me Amor · Admin
        </div>
        <h1 className="mt-3 font-serif-me text-3xl text-[#36292A] text-center">
          Sign in
        </h1>
        <p className="mt-2 text-sm text-[#7D6B6D] text-center">
          Enter your admin password to view sign-ups and orders.
        </p>

        <label className="mt-6 block text-[10px] tracking-[0.28em] uppercase text-[#7D6B6D]">
          Password
        </label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="me-input"
          placeholder="••••••••••"
          autoFocus
          data-testid="admin-password-input"
        />

        <button
          type="submit"
          disabled={loading}
          className="me-btn-primary w-full justify-center mt-6"
          data-testid="admin-login-submit"
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <div className="rounded-2xl border border-[#E8D7D0] bg-white p-5">
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#7D6B6D]">
        {label}
      </div>
      <div className="mt-2 font-serif-me text-3xl text-[#36292A]">{value}</div>
      {hint && (
        <div className="mt-1 text-[11px] tracking-[0.2em] uppercase text-[#C48B9F]">
          {hint}
        </div>
      )}
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [tab, setTab] = useState("subscribers");
  const [summary, setSummary] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stock, setStock] = useState([]);
  const [expandedWaitlist, setExpandedWaitlist] = useState(null);
  const [waitlistCache, setWaitlistCache] = useState({});
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const load = async () => {
    setLoading(true);
    try {
      const [s, subs, ords, st] = await Promise.all([
        api.get("/admin/summary", { headers: authHeaders }),
        api.get("/admin/subscribers", { headers: authHeaders }),
        api.get("/admin/orders", { headers: authHeaders }),
        api.get("/admin/stock", { headers: authHeaders }),
      ]);
      setSummary(s.data);
      setSubscribers(subs.data.subscribers || []);
      setOrders(ords.data.orders || []);
      setStock(st.data.products || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired — please sign in again.");
        onLogout();
      } else {
        console.error(e);
        toast.error("Couldn't load data. Please refresh.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const toggleStock = async (productId, nextInStock) => {
    try {
      setTogglingId(productId);
      await api.post(
        `/admin/stock/${productId}`,
        { in_stock: nextInStock },
        { headers: authHeaders }
      );
      setStock((prev) =>
        prev.map((p) =>
          p.id === productId
            ? {
                ...p,
                in_stock: nextInStock,
                override: true,
                updated_at: new Date().toISOString(),
              }
            : p
        )
      );
      toast.success(
        nextInStock ? "Restocked · now live" : "Marked as sold out"
      );
    } catch (e) {
      console.error(e);
      toast.error("Couldn't update stock. Please try again.");
    } finally {
      setTogglingId(null);
    }
  };

  const loadWaitlist = async (productId) => {
    if (waitlistCache[productId]) {
      setExpandedWaitlist(
        expandedWaitlist === productId ? null : productId
      );
      return;
    }
    try {
      const res = await api.get(`/admin/stock/${productId}/waitlist`, {
        headers: authHeaders,
      });
      setWaitlistCache((c) => ({
        ...c,
        [productId]: res.data.waitlist || [],
      }));
      setExpandedWaitlist(productId);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't load waitlist.");
    }
  };

  const exportWaitlist = (productId, productName) => {
    const rows = waitlistCache[productId] || [];
    const headers = ["email", "created_at"];
    downloadFile(
      `meamor-waitlist-${productId}.csv`,
      toCSV(
        headers,
        rows.map((r) => ({ email: r.email, created_at: r.created_at }))
      )
    );
    toast.success(`Exported ${rows.length} waitlist emails for ${productName}`);
  };

  const exportSubscribers = () => {
    const headers = ["email", "name", "created_at"];
    const rows = subscribers.map((s) => ({
      email: s.email,
      name: s.name || "",
      created_at: s.created_at,
    }));
    downloadFile("meamor-subscribers.csv", toCSV(headers, rows));
  };

  const exportOrders = () => {
    const headers = [
      "created_at",
      "customer_email",
      "amount",
      "currency",
      "payment_status",
      "items",
      "personalization_fee",
      "subscription_discount",
      "delivery_fee",
    ];
    const rows = orders.map((o) => ({
      created_at: o.created_at,
      customer_email: o.customer_email || "",
      amount: o.amount,
      currency: (o.currency || "").toUpperCase(),
      payment_status: o.payment_status,
      items: (o.line_items || [])
        .map((li) => `${li.quantity}x ${li.product_name}`)
        .join(" | "),
      personalization_fee: o.personalization_fee || 0,
      subscription_discount: o.subscription_discount || 0,
      delivery_fee: o.delivery_fee || 0,
    }));
    downloadFile("meamor-orders.csv", toCSV(headers, rows));
  };

  return (
    <div
      className="min-h-screen bg-[#FFFDF9]"
      data-testid="admin-dashboard"
    >
      <header className="border-b border-[#E8D7D0] bg-[#FFFDF9]/90 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C48B9F]">
              Me Amor
            </div>
            <div className="font-serif-me text-xl text-[#36292A]">Admin</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="w-10 h-10 rounded-full border border-[#E8D7D0] flex items-center justify-center text-[#36292A] hover:border-[#C48B9F] hover:text-[#C48B9F] transition-colors"
              onClick={load}
              aria-label="Refresh"
              data-testid="admin-refresh"
            >
              <RefreshCw size={16} />
            </button>
            <button
              className="w-10 h-10 rounded-full border border-[#E8D7D0] flex items-center justify-center text-[#36292A] hover:border-[#C48B9F] hover:text-[#C48B9F] transition-colors"
              onClick={onLogout}
              aria-label="Sign out"
              data-testid="admin-logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        {/* Summary */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <StatCard
            label="Sign-ups"
            value={summary ? summary.subscribers.total : "—"}
            hint={
              summary
                ? `+${summary.subscribers.last_7d} in last 7 days`
                : null
            }
          />
          <StatCard
            label="Paid orders"
            value={summary ? summary.orders.total_paid : "—"}
            hint={
              summary
                ? `${summary.orders.paid_last_30d} in last 30 days`
                : null
            }
          />
          <StatCard
            label="Revenue"
            value={
              summary
                ? formatNZD(summary.orders.revenue_paid_all_time)
                : "—"
            }
            hint="All time · paid"
          />
          <StatCard
            label="30-day revenue"
            value={
              summary ? formatNZD(summary.orders.revenue_last_30d) : "—"
            }
            hint="Last 30 days"
          />
        </section>

        {/* Tabs */}
        <div
          className="mt-8 border-b border-[#E8D7D0] flex items-center gap-6"
          role="tablist"
        >
          {[
            { id: "subscribers", label: `Pen Pal Club (${subscribers.length})` },
            { id: "orders", label: `Orders (${orders.length})` },
            {
              id: "stock",
              label: `Stock (${stock.filter((p) => !p.in_stock).length} out)`,
            },
          ].map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={`pb-3 -mb-px text-sm transition-colors border-b-2 ${
                tab === t.id
                  ? "border-[#C48B9F] text-[#36292A]"
                  : "border-transparent text-[#7D6B6D] hover:text-[#36292A]"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading && (
          <div className="mt-10 text-center text-[#7D6B6D] text-sm">
            Loading…
          </div>
        )}

        {!loading && tab === "subscribers" && (
          <section
            className="mt-6"
            data-testid="admin-subscribers-panel"
          >
            <div className="flex items-center justify-between gap-3 mb-4">
              <p className="text-sm text-[#7D6B6D]">
                Everyone who has joined the Pen Pal Club.
              </p>
              <button
                className="me-btn-ghost"
                onClick={exportSubscribers}
                disabled={subscribers.length === 0}
                data-testid="admin-export-subscribers"
              >
                <Download size={14} /> Export CSV
              </button>
            </div>
            {subscribers.length === 0 ? (
              <div className="border border-[#E8D7D0] rounded-2xl p-8 text-center text-[#7D6B6D]">
                No sign-ups yet.
              </div>
            ) : (
              <div className="border border-[#E8D7D0] rounded-2xl overflow-hidden bg-white">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[520px]">
                    <thead className="bg-[#F9F6F0] text-[#7D6B6D]">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium">Email</th>
                        <th className="text-left px-4 py-3 font-medium">Name</th>
                        <th className="text-left px-4 py-3 font-medium">
                          Joined
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.map((s, i) => (
                        <tr
                          key={s.email + i}
                          className="border-t border-[#E8D7D0]"
                        >
                          <td className="px-4 py-3 text-[#36292A]">
                            {s.email}
                          </td>
                          <td className="px-4 py-3 text-[#36292A]">
                            {s.name || "—"}
                          </td>
                          <td className="px-4 py-3 text-[#7D6B6D]">
                            {formatDate(s.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        )}

        {!loading && tab === "orders" && (
          <section className="mt-6" data-testid="admin-orders-panel">
            <div className="flex items-center justify-between gap-3 mb-4">
              <p className="text-sm text-[#7D6B6D]">
                All checkout sessions, newest first.
              </p>
              <button
                className="me-btn-ghost"
                onClick={exportOrders}
                disabled={orders.length === 0}
                data-testid="admin-export-orders"
              >
                <Download size={14} /> Export CSV
              </button>
            </div>
            {orders.length === 0 ? (
              <div className="border border-[#E8D7D0] rounded-2xl p-8 text-center text-[#7D6B6D]">
                No orders yet.
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map((o) => (
                  <article
                    key={o.session_id}
                    className="border border-[#E8D7D0] rounded-2xl bg-white p-4 sm:p-5"
                    data-testid={`admin-order-${o.session_id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[10px] tracking-[0.28em] uppercase text-[#7D6B6D]">
                          {formatDate(o.created_at)}
                        </div>
                        <div className="font-serif-me text-lg text-[#36292A] mt-1">
                          {o.customer_email || "(email captured at Stripe)"}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-[#7D6B6D]">
                          {(o.line_items || []).map((li, i) => (
                            <span
                              key={i}
                              className="inline-flex items-center gap-1 rounded-full bg-[#F9F6F0] px-3 py-1"
                            >
                              {li.quantity}× {li.product_name}
                              {li.recipient_age && (
                                <span className="text-[#36292A]">
                                  · age {li.recipient_age}
                                </span>
                              )}
                              {li.personalize && (
                                <span className="text-[#C48B9F]">
                                  · personalised
                                </span>
                              )}
                              {li.subscribe && (
                                <span className="text-[#C48B9F]">
                                  · subscription
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-serif-me text-xl text-[#36292A]">
                          {formatNZD(o.amount)}
                        </div>
                        <div
                          className={
                            "mt-1 inline-block rounded-full px-3 py-0.5 text-[10px] tracking-[0.24em] uppercase " +
                            (o.payment_status === "paid"
                              ? "bg-[#E7F1E1] text-[#3B7A38]"
                              : o.payment_status === "initiated"
                                ? "bg-[#F9F6F0] text-[#7D6B6D]"
                                : "bg-[#F5DDD9] text-[#8A3B39]")
                          }
                        >
                          {o.payment_status || "unknown"}
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        {!loading && tab === "stock" && (
          <section className="mt-6" data-testid="admin-stock-panel">
            <p className="text-sm text-[#7D6B6D] mb-4">
              One-click toggle for each product. Sold-out items show a waitlist
              of visitors who asked to be notified when back in stock.
            </p>
            {stock.length === 0 ? (
              <div className="border border-[#E8D7D0] rounded-2xl p-8 text-center text-[#7D6B6D]">
                No products.
              </div>
            ) : (
              <div className="space-y-3">
                {stock.map((p) => {
                  const isOpen = expandedWaitlist === p.id;
                  const rows = waitlistCache[p.id] || [];
                  return (
                    <article
                      key={p.id}
                      className="border border-[#E8D7D0] rounded-2xl bg-white p-4 sm:p-5"
                      data-testid={`admin-stock-row-${p.id}`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            {p.in_stock ? (
                              <PackageCheck
                                size={16}
                                className="text-[#3B7A38]"
                              />
                            ) : (
                              <PackageX
                                size={16}
                                className="text-[#B23A48]"
                              />
                            )}
                            <span className="font-serif-me text-lg text-[#36292A]">
                              {p.name}
                            </span>
                          </div>
                          <div className="mt-1 text-[11px] tracking-[0.24em] uppercase text-[#7D6B6D]">
                            {formatNZD(p.amount)} ·{" "}
                            {p.in_stock ? "In stock" : "Sold out"}
                            {p.override && (
                              <span className="ml-2 text-[#C48B9F]">
                                (admin override)
                              </span>
                            )}
                            {p.waitlist > 0 && (
                              <span className="ml-2 text-[#B23A48]">
                                · {p.waitlist} waiting
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {p.waitlist > 0 && (
                            <button
                              className="me-btn-ghost"
                              onClick={() => loadWaitlist(p.id)}
                              data-testid={`admin-stock-waitlist-${p.id}`}
                            >
                              {isOpen ? "Hide" : "View"} waitlist
                            </button>
                          )}
                          <button
                            className={
                              "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium tracking-wide transition-colors " +
                              (p.in_stock
                                ? "bg-[#F5DDD9] text-[#8A3B39] hover:bg-[#EFC9C3]"
                                : "bg-[#E7F1E1] text-[#3B7A38] hover:bg-[#D5E7CD]")
                            }
                            disabled={togglingId === p.id}
                            onClick={() => toggleStock(p.id, !p.in_stock)}
                            data-testid={`admin-stock-toggle-${p.id}`}
                          >
                            {togglingId === p.id
                              ? "Saving…"
                              : p.in_stock
                                ? "Mark sold out"
                                : "Restock"}
                          </button>
                        </div>
                      </div>

                      {isOpen && (
                        <div
                          className="mt-4 pt-4 border-t border-[#E8D7D0]"
                          data-testid={`admin-stock-waitlist-panel-${p.id}`}
                        >
                          <div className="flex items-center justify-between gap-2 mb-3">
                            <div className="text-[11px] tracking-[0.24em] uppercase text-[#7D6B6D]">
                              Waitlist · {rows.length}
                            </div>
                            <button
                              className="me-btn-ghost"
                              onClick={() => exportWaitlist(p.id, p.name)}
                              disabled={rows.length === 0}
                              data-testid={`admin-stock-export-${p.id}`}
                            >
                              <Download size={14} /> Export CSV
                            </button>
                          </div>
                          {rows.length === 0 ? (
                            <div className="text-sm text-[#7D6B6D]">
                              No sign-ups yet.
                            </div>
                          ) : (
                            <ul className="divide-y divide-[#E8D7D0]">
                              {rows.map((r, i) => (
                                <li
                                  key={r.email + i}
                                  className="py-2 flex items-center justify-between gap-3 text-sm"
                                >
                                  <span className="text-[#36292A] truncate">
                                    {r.email}
                                  </span>
                                  <span className="text-[#7D6B6D] text-xs whitespace-nowrap">
                                    {formatDate(r.created_at)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() =>
    localStorage.getItem(TOKEN_KEY) || null
  );

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <LoginView onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
